<template>
  <el-dialog
    :title="$t('productSimilar.title')"
    :visible.sync="visible"
    width="980px"
    append-to-body
    :close-on-click-modal="false"
    @open="open"
    @close="close"
  >
    <div class="similar-dialog" v-loading="loading" :element-loading-text="$t('common.loading')">
      <div class="similar-panel selected-panel">
        <div class="panel-title">{{ $t("productSimilar.selectedProducts") }}</div>
        <el-input
          v-model="selectedKeyword"
          size="small"
          :placeholder="$t('searchProduct.goodsName')"
          class="filter-input"
          clearable
        ></el-input>
        <div class="product-list selected-list">
          <div v-for="product in filteredSelectedProducts" :key="product.goodsId" class="product-card selected-card">
            <el-image class="product-image" :src="product.image" fit="contain"></el-image>
            <div class="product-info">
              <div class="product-name">{{ product.goodsName }}</div>
              <el-tag v-if="product.currentGoods" size="mini" type="success">{{
                $t("productSimilar.currentProduct")
              }}</el-tag>
            </div>
            <el-button
              v-if="!product.currentGoods"
              type="text"
              class="remove-button"
              @click="removeSelected(product.goodsId)"
              >{{ $t("common.delete") }}</el-button
            >
          </div>
          <el-empty
            v-if="filteredSelectedProducts.length === 0"
            :image-size="90"
            :description="$t('common.noData')"
          ></el-empty>
        </div>
      </div>
      <div class="similar-panel candidate-panel">
        <div class="candidate-header">
          <div class="panel-title">{{ $t("productSimilar.candidateProducts") }}</div>
          <div>
            <el-input
              v-model="candidateKeyword"
              size="small"
              :placeholder="$t('searchProduct.goodsName')"
              class="candidate-search"
              clearable
              @clear="searchCandidates"
              @change="searchCandidates"
              @keyup.enter.native="searchCandidates"
            ></el-input>
            <el-button type="primary" size="small" icon="el-icon-search" @click="searchCandidates">{{
              $t("common.search")
            }}</el-button>
          </div>
        </div>
        <div class="product-list candidate-list">
          <div
            v-for="product in candidateProducts"
            :key="product.goodsId"
            :class="[
              'product-card',
              'candidate-card',
              { active: isSelected(product.goodsId), disabled: isCandidateDisabled(product) }
            ]"
            @click="toggleCandidate(product)"
          >
            <el-image class="product-image" :src="product.image" fit="contain"></el-image>
            <div class="product-info">
              <div class="product-name">{{ product.goodsName }}</div>
              <div class="product-tags">
                <el-tag v-if="isCurrentSimilarGroupProduct(product)" size="mini" type="warning">{{
                  $t("productSimilar.hasSimilarGroup")
                }}</el-tag>
                <el-tag v-else-if="isCandidateDisabled(product)" size="mini" type="info">{{
                  $t("productSimilar.configuredSimilarProduct")
                }}</el-tag>
                <el-tag v-if="isSelected(product.goodsId)" size="mini" type="success">{{
                  $t("productSimilar.selected")
                }}</el-tag>
              </div>
            </div>
          </div>
          <el-empty
            v-if="candidateProducts.length === 0"
            :image-size="90"
            :description="$t('common.noData')"
          ></el-empty>
        </div>
        <el-pagination
          v-if="candidateProducts.length > 0"
          background
          layout="prev, pager, next"
          :current-page="candidatePage.pageIndex"
          :total="candidatePage.totalSize"
          :page-size="candidatePage.pageSize"
          @current-change="candidatePageChange"
        ></el-pagination>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="close">{{ $t("common.cancel") }}</el-button>
      <el-button size="small" type="primary" :loading="saveLoading" @click="saveSimilarGroup">{{
        $t("common.definite")
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: "ProductSimilarDialog",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      saveLoading: false,
      groupName: "",
      similarGroupId: "",
      selectedProducts: [],
      selectedKeyword: "",
      candidateProducts: [],
      candidateKeyword: "",
      candidatePage: {
        pageSize: 8,
        pageIndex: 1,
        totalSize: 0
      }
    };
  },
  computed: {
    visible: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.close();
        }
      }
    },
    goodsId() {
      return this.product.id || this.product.goodsId || this.product.productId;
    },
    filteredSelectedProducts() {
      const keyword = this.normalizeSearchText(this.selectedKeyword);
      if (!keyword) return this.selectedProducts;
      return this.selectedProducts.filter(product => this.isFuzzyMatch(product.goodsName, keyword));
    }
  },
  methods: {
    async open() {
      this.groupName = "";
      this.similarGroupId = "";
      this.selectedProducts = [];
      this.selectedKeyword = "";
      this.candidateProducts = [];
      this.candidateKeyword = "";
      this.candidatePage.pageIndex = 1;
      this.loading = true;
      await Promise.all([this.fetchDetail(), this.fetchCandidates()]);
      this.loading = false;
    },
    normalizeProduct(item) {
      const goodsId = item.goodsId || item.productId || item.id || "";
      return {
        ...item,
        goodsId,
        goodsName: item.goodsName || item.productName || item.name || "",
        image: item.image || item.goodsUrl || item.url || "",
        currentGoods: item.currentGoods || item.currentProduct || false
      };
    },
    async fetchDetail() {
      if (!this.goodsId) return;
      try {
        const res = await this.$api.request(this.$url.ProductSimilar.detail, this.$method.get, {
          goodsId: this.goodsId
        });
        if (res.code === 0 && res.data) {
          this.groupName = res.data.similarGroupName || "";
          this.similarGroupId = res.data.similarGroupId || "";
          this.selectedProducts = (res.data.similarGoods || []).map(this.normalizeProduct);
          return;
        }
        this.$message.warning(res.msg || this.$t("productSimilar.detailFailed"));
      } catch (error) {
        this.$message.error(this.$t("productSimilar.detailFailed"));
      }
    },
    async fetchCandidates() {
      try {
        const res = await this.$api.request(this.$url.OpenDoor.getSkuList, this.$method.get, {
          pageIndex: this.candidatePage.pageIndex,
          pageSize: this.candidatePage.pageSize,
          goodsName: this.candidateKeyword,
          skuResult: 2
        });
        if (res.code === 0 && res.data) {
          this.candidateProducts = (res.data.records || []).map(this.normalizeProduct);
          this.candidatePage.totalSize = parseInt(res.data.total || 0);
          return;
        }
        this.$message.warning(res.msg || this.$t("productSimilar.candidateFailed"));
      } catch (error) {
        this.$message.error(this.$t("productSimilar.candidateFailed"));
      }
    },
    async searchCandidates() {
      this.candidatePage.pageIndex = 1;
      this.loading = true;
      await this.fetchCandidates();
      this.loading = false;
    },
    async candidatePageChange(pageIndex) {
      this.candidatePage.pageIndex = pageIndex;
      this.loading = true;
      await this.fetchCandidates();
      this.loading = false;
    },
    normalizeSearchText(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");
    },
    isFuzzyMatch(value, keyword) {
      const text = this.normalizeSearchText(value);
      if (!keyword || text.includes(keyword)) return true;
      let index = 0;
      for (let i = 0; i < text.length && index < keyword.length; i++) {
        if (text[i] === keyword[index]) index++;
      }
      return index === keyword.length;
    },
    isSelected(goodsId) {
      return this.selectedProducts.some(product => product.goodsId === goodsId);
    },
    isValidSimilarGroupId(similarGroupId) {
      return similarGroupId !== undefined && similarGroupId !== null && similarGroupId !== "" && similarGroupId !== 0 && similarGroupId !== "0";
    },
    getSimilarGroupKey(similarGroupId) {
      return this.isValidSimilarGroupId(similarGroupId) ? String(similarGroupId) : "";
    },
    isCurrentSimilarGroupProduct(product) {
      const currentGroupKey = this.getSimilarGroupKey(this.similarGroupId);
      return currentGroupKey && this.getSimilarGroupKey(product.similarGroupId) === currentGroupKey;
    },
    isCandidateDisabled(product) {
      return product.hasSimilarGroup === true && !this.isCurrentSimilarGroupProduct(product);
    },
    toggleCandidate(product) {
      if (!product || product.goodsId === this.goodsId || this.isCandidateDisabled(product)) return;
      if (this.isSelected(product.goodsId)) {
        this.removeSelected(product.goodsId);
        return;
      }
      this.selectedProducts.push({
        goodsId: product.goodsId,
        goodsName: product.goodsName,
        image: product.image,
        currentGoods: false
      });
    },
    removeSelected(goodsId) {
      this.selectedProducts = this.selectedProducts.filter(product => product.goodsId !== goodsId);
    },
    async saveSimilarGroup() {
      if (!this.goodsId) return;
      const similarGoodsIds = this.selectedProducts
        .filter(product => product.goodsId !== this.goodsId)
        .map(product => product.goodsId);
      const confirmed = await this.confirmSimilarWarning(similarGoodsIds);
      if (!confirmed) return;
      this.saveLoading = true;
      try {
        const res = await this.$api.request(this.$url.ProductSimilar.save, this.$method.post, {
          goodsId: this.goodsId,
          similarGoodsIds,
          groupName: this.groupName
        });
        if (res.code === 0) {
          this.$message.success(res.msg || this.$t("productSimilar.saveSuccess"));
          this.$emit("refresh");
          this.close();
          return;
        }
        this.$message.warning(res.msg || this.$t("productSimilar.saveFailed"));
      } catch (error) {
        this.$message.error(this.$t("productSimilar.saveFailed"));
      } finally {
        this.saveLoading = false;
      }
    },
    confirmSimilarWarning(similarGoodsIds) {
      if (similarGoodsIds.length === 0) return Promise.resolve(true);
      return this.$confirm(this.$t("productSimilar.recognitionWarning"), this.$t("common.tip"), {
        confirmButtonText: this.$t("common.definite"),
        cancelButtonText: this.$t("common.cancel"),
        type: "warning"
      })
        .then(() => true)
        .catch(() => false);
    },
    close() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.similar-dialog {
  display: flex;
  gap: 16px;
  min-height: 460px;
}
.similar-panel {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
  min-width: 0;
}
.selected-panel {
  width: 42%;
}
.candidate-panel {
  flex: 1;
}
.panel-title {
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}
.filter-input {
  margin-bottom: 10px;
}
.candidate-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}
.candidate-search {
  width: 180px;
  margin-right: 6px;
}
.product-list {
  display: grid;
  gap: 10px;
  max-height: 370px;
  overflow-y: auto;
}
.candidate-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.product-card {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
  min-height: 72px;
  background: var(--vtm-surface);
}
.candidate-card {
  cursor: pointer;
}
.candidate-card.active {
  border-color: #67c23a;
  background: #f0f9eb;
}
.candidate-card.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.product-image {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
}
.product-info {
  min-width: 0;
  flex: 1;
}
.product-name {
  color: #303133;
  line-height: 18px;
  word-break: break-word;
}
.product-tags {
  margin-top: 6px;
  min-height: 20px;
}
.product-tags .el-tag {
  margin-right: 4px;
}
.remove-button {
  flex: 0 0 auto;
  color: #f56c6c;
}
</style>
